<script context="module">
  // Module-scoped cache so a single `get_meta()` per backend URL is shared
  // across every PlaygroundPanel mount (the panel re-mounts on every
  // selection cycle in standalone). Successful results stay cached for
  // the page lifetime; failures are NOT cached so a transient network
  // blip doesn't permanently hide the chip.
  const metaCache = new Map();
</script>

<script>
  import { onMount, onDestroy } from 'svelte';
  import { transform } from 'ol/proj';
  import { X, Share2, Check, ChevronDown, ChevronUp, ChevronRight, Image, Package, Navigation, Star, Info, Phone, Mail, Accessibility, PencilLine } from 'lucide-svelte';
  import { _, locale } from 'svelte-i18n';

  import { selection } from '../stores/selection.js';
  import { mapStore } from '../stores/map.js';
  import { location } from '../stores/location.js';
  import { mappingDetail, DETAIL_PARAM } from '../stores/mappingDetail.js';
  import { fetchPlaygroundEquipment, fetchNearbyPOIs, fetchTrees, fetchMeta } from '../lib/api.js';
  import { overlayFeaturesStore } from '../stores/overlayLayer.js';
  import { groupEquipment } from '../lib/equipmentGrouping.js';
  import { playgroundCompleteness, mappingDetailHintKey } from '../lib/completeness.js';
  import { openingHoursState } from '../lib/openingHours.js';
  import { litInfo } from '../lib/pitchInfo.js';
  import { COMPLETENESS_PALETTE } from '../lib/completenessPalette.js';
  import { poiRadiusM, appMode, mapMinZoom, regionLang } from '../lib/config.js';
  import { getPlaygroundTitle, getPlaygroundLocation, hasOsmName, navigationUrl, isMobileUserAgent } from '../lib/playgroundHelpers.js';
  import { aggregatePlaygroundThemes, areaThemesOf, themeIcon, themeName } from '../lib/playgroundThemes.js';
  import { summarizeEquipment } from '../lib/equipmentSummary.js';
  import { cn } from '../lib/utils.js';
  import EquipmentList from './EquipmentList.svelte';
  import MapCompleteLink from './MapCompleteLink.svelte';
  import POIPanel from './POIPanel.svelte';
  import PanoramaxViewer from './PanoramaxViewer.svelte';
  import CommonsGallery from './CommonsGallery.svelte';
  import ReviewsPanel from './ReviewsPanel.svelte';
  import { radiusFromExtent } from '../lib/reviews.js';
  import Badge from './ui/Badge.svelte';
  import Button from './ui/Button.svelte';

  /** When true, renders without the fixed sidebar wrapper (for bottom sheet embedding) */
  export let embedded = false;

  // ── Derived state from selection store ────────────────────────────────────
  // Playgrounds only: a selected pitch has its own panel (#934), and loading
  // equipment, trees and reviews for it would be wrong.
  $: feature     = $selection.kind === 'playground' ? $selection.feature : null;
  $: backendUrl  = $selection.backendUrl;
  $: attr        = feature ? feature.getProperties() : null;
  // An OSM name is a proper noun in the region's language; the `nearby.defaultName`
  // placeholder is interface text. Without this the heading inherits the document
  // language, which is the UI locale (WCAG 3.1.2).
  $: titleLang   = hasOsmName(attr) ? regionLang : $locale;
  $: backendName = appMode === 'hub' ? (feature?.get('_backendName') ?? null) : null;

  // ── Async data ────────────────────────────────────────────────────────────
  let equipmentFeatures = [];
  let equipmentGroups = [];

  // ── Playground themes (area tag + themed devices), deduped + ordered ───────
  const THEME_CAP = 4;
  $: deviceProps = [
    ...equipmentFeatures.map(f => f.properties),
    ...equipmentGroups.flatMap(g => [g.structure.properties, ...g.children.map(c => c.properties)]),
  ];
  $: themes = attr ? aggregatePlaygroundThemes(attr, deviceProps) : [];
  // The first area-tag theme gets a prominent banner ("this is an octopus
  // playground"); every other theme — remaining area themes plus device-derived
  // ones — becomes a chip. Only the banner theme is excluded from the chip row,
  // so a multi-value area tag (`ship;castle`) never drops its extra themes.
  $: areaThemes = attr ? areaThemesOf(attr) : [];
  $: areaTheme = areaThemes[0] ?? null;
  $: deviceThemes = themes.filter(v => v !== areaTheme);
  $: visibleThemes = deviceThemes.slice(0, THEME_CAP);
  $: themeOverflow = Math.max(0, deviceThemes.length - THEME_CAP);

  // Equipment count summary, surfaced in the overview (not buried in the section).
  $: equipSummary = summarizeEquipment(equipmentFeatures, equipmentGroups);
  $: equipSummaryItems = [
    ['deviceCount', equipSummary.devices],
    ['fitnessCount', equipSummary.fitness],
    ['benches', equipSummary.benches],
    ['shelters', equipSummary.shelters],
    ['picnic', equipSummary.picnic],
  ].filter(([, n]) => n > 0);
  let pois = [];
  let treeRows = [];
  let equipmentLoading = false;
  let poisLoading = false;

  // ── Backend metadata (data age) ───────────────────────────────────────────
  // metaCache is module-scoped (see <script context="module"> above) so the
  // get_meta() result survives the panel's per-selection remount cycle.
  let osmDataAgeSec = null;
  // Generation guard so a slow earlier loadMeta() can't overwrite a faster
  // later one when the user selects two playgrounds in quick succession.
  let metaGen = 0;

  async function loadMeta(url) {
    const gen = ++metaGen;
    if (!url) { osmDataAgeSec = null; return; }
    if (metaCache.has(url)) {
      if (gen === metaGen) osmDataAgeSec = metaCache.get(url);
      return;
    }
    try {
      const meta = await fetchMeta(url);
      // Prefer the user-facing OSM snapshot age; fall back to import-run age.
      const ageSec = meta?.osm_data_age_seconds ?? meta?.data_age_seconds ?? null;
      metaCache.set(url, ageSec);
      if (gen === metaGen) osmDataAgeSec = ageSec;
    } catch {
      // Don't cache failures — a transient network error must not permanently
      // hide the chip for this backend URL.
      if (gen === metaGen) osmDataAgeSec = null;
    }
  }

  function formatDataAge(sec) {
    if (!Number.isFinite(sec) || sec < 0) return null;
    if (sec < 60) return $_('hub.dataAgeJustNow');
    const m = Math.round(sec / 60);
    if (m < 60)   return $_('hub.dataAgeMinutes', { values: { m } });
    const h = Math.round(m / 60);
    if (h < 48)   return $_('hub.dataAgeHours',   { values: { h } });
    const d = Math.round(h / 24);
    if (d <= 365) return $_('hub.dataAgeDays',    { values: { d } });
    const y = Math.round(d / 365);
    return $_('hub.dataAgeYears', { values: { y } });
  }

  $: loadMeta(backendUrl);
  $: dataAgeFormatted = formatDataAge(osmDataAgeSec);
  // Close the popover when the user picks a different playground / backend
  // — the popover's body text references `dataAgeFormatted`, which would
  // otherwise show stale content next to the new title until the user
  // dismisses the popover.
  $: if (backendUrl !== undefined) dataAgePopoverOpen = false;

  // ── Data age popover ──────────────────────────────────────────────────────
  let dataAgePopoverOpen = false;
  let dataAgePopoverStyle = '';
  let chipEl;

  function recomputePopoverStyle() {
    if (!chipEl) return;
    const chip = chipEl.getBoundingClientRect();
    // Clamp right edge against the panel's right boundary; floor the width
    // so we never compute a zero/negative width on very narrow viewports.
    const panel = chipEl.closest('aside, [data-panel]');
    const panelRight = panel ? panel.getBoundingClientRect().right : window.innerWidth;
    const maxWidth = Math.max(140, Math.min(260, panelRight - chip.left - 12));
    dataAgePopoverStyle = [
      `top:${chip.bottom + 6}px`,
      `left:${chip.left}px`,
      `width:${maxWidth}px`,
    ].join(';');
  }

  function onDocumentClick(e) {
    // Don't close when the click is inside the popover (so users can select
    // text) or on the chip button itself (its own onclick handles toggle).
    if (e.target?.closest?.('.data-age-popover, .data-age-chip')) return;
    closeDataAgePopover();
  }
  function onPopoverKeydown(e) {
    if (e.key === 'Escape' && dataAgePopoverOpen) {
      // Stop propagation so the panel-level Escape handler doesn't also fire
      // and close the entire panel.
      e.stopPropagation();
      closeDataAgePopover();
    }
  }
  function onViewportChange() {
    // Scroll/resize/sheet-drag invalidate the fixed-position style. Cheaper
    // and less surprising to close than to chase the chip around the screen.
    closeDataAgePopover();
  }

  function openDataAgePopover() {
    recomputePopoverStyle();
    dataAgePopoverOpen = true;
    // Defer listener attachment by a tick so the click that opened the
    // popover doesn't immediately close it via document-level capture.
    setTimeout(() => {
      window.addEventListener('click', onDocumentClick);
      window.addEventListener('keydown', onPopoverKeydown);
      window.addEventListener('resize', onViewportChange);
      // capture: catches scroll on any ancestor (panel, bottom sheet, page)
      window.addEventListener('scroll', onViewportChange, true);
    }, 0);
  }

  function closeDataAgePopover() {
    if (!dataAgePopoverOpen) return;
    dataAgePopoverOpen = false;
    window.removeEventListener('click', onDocumentClick);
    window.removeEventListener('keydown', onPopoverKeydown);
    window.removeEventListener('resize', onViewportChange);
    window.removeEventListener('scroll', onViewportChange, true);
  }

  function toggleDataAgePopover(e) {
    e.stopPropagation();
    if (dataAgePopoverOpen) closeDataAgePopover();
    else                    openDataAgePopover();
  }

  // Centre of selected playground in WGS84
  let centerLat = 0, centerLon = 0;
  // The Mangrove subject's radius (#914), rule in lib/reviews.js.
  let reviewRadiusM = null;

  // Generation counter — incremented on each new selection so stale responses are discarded.
  let loadGen = 0;

  $: if (feature) {
    loadData(feature, backendUrl);
  } else {
    equipmentFeatures = [];
    equipmentGroups = [];
    pois = [];
    treeRows = [];
    overlayFeaturesStore.set({ equipment: [], trees: [] });
  }

  async function loadData(feat, url) {
    const geom = feat.getGeometry();
    if (!geom) { equipmentFeatures = []; pois = []; return; }

    const gen = ++loadGen;
    const ext = geom.getExtent();
    const [lon, lat] = transform(
      [(ext[0] + ext[2]) / 2, (ext[1] + ext[3]) / 2],
      'EPSG:3857', 'EPSG:4326'
    );
    centerLat = lat;
    centerLon = lon;
    reviewRadiusM = radiusFromExtent(ext);

    equipmentLoading = true;
    poisLoading = true;

    let localEquipment = [];
    let localTrees = [];

    try {
      const geojson = await fetchPlaygroundEquipment(ext, feat.get('osm_id'), url);
      if (gen === loadGen) {
        localEquipment = geojson.features ?? [];
        // Clear any leftover _groupId tags before re-stamping. The current
        // fetch path returns fresh objects per call so this is defensive,
        // but a future caching layer or a hub-mode merge that reuses
        // feature references would otherwise leak stale group membership
        // (and silently hide the dot for a now-standalone device).
        for (const f of localEquipment) {
          if (f.properties) delete f.properties._groupId;
        }
        const { groups, standalone } = groupEquipment(localEquipment);
        for (const { structure, children } of groups) {
          for (const child of children) {
            child.properties._groupId = structure.properties.osm_id;
          }
        }
        equipmentGroups = groups;
        equipmentFeatures = standalone;
      }
    } catch (err) {
      console.warn('[panel] Equipment load failed:', err);
      if (gen === loadGen) {
        equipmentFeatures = [];
        equipmentGroups = [];
        treeRows = [];
        // Clear stale equipment from the previous selection's overlay so
        // the map doesn't keep showing wrong dots on the new playground.
        overlayFeaturesStore.set({ equipment: [], trees: [] });
      }
    } finally {
      if (gen === loadGen) equipmentLoading = false;
    }

    try {
      const treeGeojson = await fetchTrees(ext, url);
      if (gen === loadGen) {
        localTrees = treeGeojson.features ?? [];
        treeRows = localTrees
          .filter(f => f.properties?.feature_type === 'tree_row' && f.properties?.length_m != null)
          .map(f => f.properties.length_m);
      }
    } catch (err) {
      // Trees silently ignored on error (e.g. Overpass/dev mode)
    }

    if (gen === loadGen) {
      overlayFeaturesStore.set({ equipment: localEquipment, trees: localTrees });
    }

    try {
      const result = await fetchNearbyPOIs(lat, lon, poiRadiusM, feat.get('osm_id'), url);
      if (gen === loadGen) pois = result;
    } catch (err) {
      console.warn('POI load failed:', err);
      if (gen === loadGen) pois = [];
    } finally {
      if (gen === loadGen) poisLoading = false;
    }
  }

  // Close the desktop side panel: clear the selection and zoom out a few steps
  // (keeping the playground centred) so the user lands back on a browsable view.
  // Similar in spirit to the mobile "back to map" gesture, which zooms out a
  // touch further (MOBILE_BACK_ZOOM_OUT) since the phone overview needs more
  // context. On mobile the panel is embedded and AppShell owns the back gesture,
  // so we only zoom out when !embedded.
  const DESKTOP_CLOSE_ZOOM_OUT = 4;
  function closePanel() {
    const view = embedded ? null : $mapStore?.getView();
    selection.clear();
    if (view) {
      const z = view.getZoom();
      if (Number.isFinite(z)) {
        view.animate({ zoom: Math.max(z - DESKTOP_CLOSE_ZOOM_OUT, mapMinZoom), duration: 400 });
      }
    }
  }

  // ── ESC to close ──────────────────────────────────────────────────────────
  function handleKeydown(e) {
    // The data-age popover has its own Escape handler. Both listeners sit on
    // `window`; stopPropagation there can't stop this sibling, and we're
    // registered first — so bail while the popover is open or Escape would
    // close the whole panel (and zoom the map out) instead of just the popover.
    if (dataAgePopoverOpen) return;
    if (e.key === 'Escape' && feature && !embedded) closePanel();
  }

  onMount(() => {
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  });

  onDestroy(() => {
    overlayFeaturesStore.set({ equipment: [], trees: [] });
    // Bail out cleanly if unmount happens while popover is open.
    closeDataAgePopover();
  });

  // ── Mapping-detail badge ──────────────────────────────────────────────────
  // Tinted from the shared map palette rather than a semantic Badge variant,
  // so the badge colour always equals the polygon the user just tapped. The
  // old success/warning/destructive variants encoded a verdict ("this one is
  // bad"), which is exactly the reading being removed (#733).
  //
  // The labels live in the `mappingDetail.*` namespace ("detailed" / "basic" /
  // "no details yet") because the value describes how much has been MAPPED,
  // not how good the playground is.
  const MAPPING_DETAIL_KEY = {
    complete: 'mappingDetail.detailed',
    partial:  'mappingDetail.basic',
    missing:  'mappingDetail.noDetails',
  };
  // Only while mapping detail is on: in the finder view the badge would bring
  // back exactly the data-quality reading the view leaves out (#924).
  $: completenessLevel = attr && $mappingDetail ? playgroundCompleteness(attr) : null;

  // Under the badge: which of the rule's two inputs is missing — so "basic" or
  // "no details yet" reads as an invitation, not a grade (#919). Same condition as the badge: only with
  // mapping detail on; nothing for "detailed".
  $: detailHintKey = completenessLevel ? mappingDetailHintKey(attr) : null;
  $: completeness = completenessLevel
    ? {
        key: MAPPING_DETAIL_KEY[completenessLevel],
        // Tinted background rather than the solid base: dark text on solid
        // #15803d is unreadable. The dot carries the identity colour, so the
        // badge still ties visually to the legend swatches and ring segments.
        style: `background: ${COMPLETENESS_PALETTE[completenessLevel].fill};`
             + `border-color: ${COMPLETENESS_PALETTE[completenessLevel].stroke};`
             + 'color: #1f2937;',
        dot: COMPLETENESS_PALETTE[completenessLevel].base,
      }
    : null;

  // ── Opening hours ─────────────────────────────────────────────────────────
  $: openingHoursInfo = attr?.opening_hours ? openingHoursState(attr.opening_hours, $_) : null;

  // ── Attribute helpers ─────────────────────────────────────────────────────
  $: accessLabel = (() => {
    if (!attr) return '';
    const privateVal = attr.private;
    if (privateVal === 'residents') return $_('details.access.residents');
    if (privateVal === 'students')  return $_('details.access.students');
    if (privateVal === 'employees') return $_('details.access.employees');
    const access = attr.access ?? 'unknown';
    return $_('details.access.' + access, { default: $_('details.access.unknown') });
  })();

  $: litFact = attr ? litInfo(attr.lit) : null;

  $: surfaceLabel = attr?.surface
    ? attr.surface.split(';').map(s => $_('details.surfaceValues.' + s.trim(), { default: s.trim() })).join(' / ')
    : null;

  $: descriptionParts = (() => {
    if (!attr) return [];
    const parts = [];
    const desc = attr['description:de'] && attr.description !== attr['description:de']
      ? (attr.description ? `${attr.description} | ${attr['description:de']}` : attr['description:de'])
      : attr.description;
    if (desc) parts.push(desc);
    if (attr.note) parts.push(`${attr.note}`);
    if (attr.fixme) parts.push(`${attr.fixme}`);
    return parts;
  })();

  $: mcOsmType = attr ? ({ W:'way', R:'relation', N:'node' }[attr.osm_type] ?? 'way') : 'way';
  $: mcUrl = attr ? `https://mapcomplete.org/playgrounds.html#${mcOsmType}/${attr.osm_id}` : '';

  // ── Accordion state ───────────────────────────────────────────────────────
  let openSections = new Set(['photos', 'equipment', 'pois']);
  function toggleSection(id) {
    const next = new Set(openSections);
    next.has(id) ? next.delete(id) : next.add(id);
    openSections = next;
  }

  // ── Panoramax UUIDs from OSM tags ─────────────────────────────────────────
  $: panoramaxUuids = (() => {
    if (!attr) return [];
    const uuids = [];
    if (attr['panoramax']) uuids.push(attr['panoramax']);
    for (let i = 1; i <= 9; i++) {
      const v = attr[`panoramax:${i}`];
      if (v) uuids.push(v);
    }
    return uuids;
  })();

  // ── Wikimedia Commons photos from OSM tags (issue #650) ────────────────────
  $: commonsTag = attr?.['wikimedia_commons'] || '';
  $: imageTag = attr?.['image'] || '';
  // How many photos the Commons gallery actually resolved (a tag can be present
  // but yield nothing: unsafe/non-Wikimedia image URL, empty category, or a
  // failed fetch). Bound from CommonsGallery so the Panoramax fallback below
  // reflects what is really on screen, not just tag presence.
  let commonsPhotoCount = 0;
  // True only when a tag is present AND the gallery actually rendered photos.
  // Tag-only-but-empty (or no tag at all) leaves this false so the Panoramax
  // "add a photo" prompt is never orphaned behind a blank gallery.
  $: commonsHasPhotos = (commonsTag || imageTag) && commonsPhotoCount > 0;

  // ── Share button ──────────────────────────────────────────────────────────
  let shareConfirmed = false;
  let shareTimeout;

  async function sharePlayground() {
    if (!attr) return;
    // Carry mapping detail only while it is on, so a contributor shares the
    // view they are looking at and everyone else's links stay as before (#924).
    const detail = $mappingDetail ? `?${DETAIL_PARAM}=1` : '';
    const url = `${window.location.origin}${window.location.pathname}${detail}#${attr.osm_type}${attr.osm_id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: getPlaygroundTitle(attr, $_), url });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
      } else {
        // fallback for non-secure contexts
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      shareConfirmed = true;
      clearTimeout(shareTimeout);
      shareTimeout = setTimeout(() => { shareConfirmed = false; }, 2000);
    } catch (err) {
      if (err.name !== 'AbortError') console.warn('Share failed:', err);
    }
  }

  // ── Navigation link ──────────────────────────────────────────────────────
  // Mobile: geo: URL opens native navigation app
  // Desktop: OSM directions with stored GPS fix as origin (falls back to empty origin
  // when location was never granted — OSM then prompts the user for a starting point).
  // The href is reactive on the location store, so the origin follows every
  // watchPosition update rather than being captured once.
  //
  // Rendered as a plain <a href>, not window.open(): iOS Safari silently drops a
  // geo: URL opened via window.open(url, '_blank'), while the same URL as a
  // same-tab link hands off to the maps app — as POIPanel's rows already did (#926).
  // URL rules shared with the pitch panel: lib/playgroundHelpers.js.
  const navIsMobile = isMobileUserAgent(navigator.userAgent);
  $: navUrl = attr
    ? navigationUrl({
        lat: centerLat, lon: centerLon,
        title: getPlaygroundTitle(attr, $_),
        mobile: navIsMobile,
        origin: $location,
      })
    : null;
</script>

{#if feature && attr}
  <aside class={cn(
    embedded ? '' : 'info-panel',
    embedded ? '' : 'lg:flex'
  )}>
    <!-- Header -->
    {#if !embedded}
      <div class="info-panel__header">
        <div class="flex-1 min-w-0">
          <h2 class="panel-title" lang={titleLang}>{getPlaygroundTitle(attr, $_)}</h2>
          {#if getPlaygroundLocation(attr, $_)}
            <p class="text-sm text-muted-foreground mt-0.5">{getPlaygroundLocation(attr, $_)}</p>
          {/if}
          {#if backendName}
            <p class="backend-name">{backendName}</p>
          {/if}
        </div>
        <div class="flex items-center gap-1 shrink-0">
          {#if navUrl}
            <a
              class="navigate-btn"
              href={navUrl}
              target={navIsMobile ? undefined : '_blank'}
              rel={navIsMobile ? undefined : 'noopener'}
            >{$_('info.navigateHere')}</a>
          {/if}
          <button class="panel-icon-btn" onclick={closePanel} aria-label={$_('info.closeBtn')}>
            <X class="h-5 w-5" />
          </button>
        </div>
      </div>
    {:else}
      <!-- Embedded header (mobile full-screen panel) -->
      <div class="flex items-start justify-between gap-2 mb-4">
        <div class="flex-1 min-w-0">
          <h2 class="panel-title" lang={titleLang}>{getPlaygroundTitle(attr, $_)}</h2>
          {#if getPlaygroundLocation(attr, $_)}
            <p class="text-sm text-muted-foreground mt-0.5">{getPlaygroundLocation(attr, $_)}</p>
          {/if}
          {#if backendName}
            <p class="backend-name">{backendName}</p>
          {/if}
        </div>
        <div class="flex items-center gap-1">
          {#if navUrl}
            <a
              class="navigate-btn"
              href={navUrl}
              target={navIsMobile ? undefined : '_blank'}
              rel={navIsMobile ? undefined : 'noopener'}
            >{$_('info.navigateHere')}</a>
          {/if}
          <button class="panel-icon-btn shrink-0" onclick={sharePlayground} aria-label={$_('info.copyLink')}>
            {#if shareConfirmed}
              <Check class="h-5 w-5 text-primary" />
            {:else}
              <Share2 class="h-5 w-5" />
            {/if}
          </button>
        </div>
      </div>
    {/if}

    <div class={cn(embedded ? '' : 'info-panel__body')}>
      <!-- Area-level theme banner: the whole playground is themed -->
      {#if areaTheme}
        <div class="theme-banner mb-3">
          <span class="theme-banner__icon" aria-hidden="true">{themeIcon(areaTheme)}</span>
          <span class="theme-banner__label">{$_('details.themedPlayground', { values: { theme: themeName(areaTheme, $_) } })}</span>
        </div>
      {/if}

      <!-- Description -->
      {#each descriptionParts as part}
        <p class="text-sm text-muted-foreground italic mb-3">{part}</p>
      {/each}

      <!-- Mapping detail + data age -->
      {#if completeness || dataAgeFormatted}
        <div class="status-row mb-4">
          {#if completeness}
            <Badge variant="outline" class="gap-1.5" style={completeness.style}>
              <span class="detail-dot" style="background: {completeness.dot};" aria-hidden="true"></span>
              {$_(completeness.key)}
            </Badge>
          {/if}
          {#if dataAgeFormatted}
            <button
              bind:this={chipEl}
              class="data-age-chip"
              onclick={toggleDataAgePopover}
              title={$_('details.osmDataAgeTitle')}
              aria-haspopup="dialog"
              aria-controls="data-age-popover"
              aria-expanded={dataAgePopoverOpen}
            >
              <Info class="h-3 w-3" />
              {$_('details.osmDataAgeChip', { values: { age: dataAgeFormatted } })}
              {#if dataAgePopoverOpen}
                <ChevronUp class="h-3 w-3" />
              {:else}
                <ChevronDown class="h-3 w-3" />
              {/if}
            </button>
          {/if}
        </div>
        {#if detailHintKey}
          <p class="detail-hint" data-testid="mapping-detail-hint">
            <PencilLine class="h-4 w-4 shrink-0 mt-px text-blue-600" />
            <span class="min-w-0">{$_(detailHintKey)}</span>
          </p>
        {/if}
      {/if}

      <!-- Status pills: opening hours, baby/toddler, shade -->
      {#if openingHoursInfo || attr.for_baby || attr.for_toddler || attr.wheelchair_play || attr.has_shade != null}
        <div class="status-row mb-4">
          {#if openingHoursInfo}
            <Badge
              variant={openingHoursInfo.open ? 'successSoft' : 'dangerSoft'}
              class="gap-[5px] min-w-0 max-w-full overflow-hidden whitespace-nowrap"
              aria-label={openingHoursInfo.open === null
                ? openingHoursInfo.text
                : `${openingHoursInfo.open ? $_('poi.open') : $_('poi.closed')}: ${openingHoursInfo.text}`}
            >
              <span class="status-dot" class:status-dot--open={openingHoursInfo.open} class:status-dot--closed={!openingHoursInfo.open} aria-hidden="true"></span>
              <span class="truncate">{openingHoursInfo.text}</span>
            </Badge>
          {/if}
          {#if attr.for_baby}
            <Badge variant="info" class="whitespace-nowrap">{$_('details.forBaby')}</Badge>
          {/if}
          {#if attr.for_toddler}
            <Badge variant="info" class="whitespace-nowrap">{$_('details.forToddler')}</Badge>
          {/if}
          <!-- Tri-state: a badge when suitable equipment is tagged, a plain note
               when someone surveyed and found none, nothing at all when no play
               device carries a wheelchair tag. The third case is unknown, not a
               negative, and must not read as one. -->
          {#if attr.wheelchair_play === 'yes'}
            <Badge variant="info" class="gap-[5px] whitespace-nowrap">
              <Accessibility class="h-3 w-3" aria-hidden="true" />{$_('details.wheelchairPlay')}
            </Badge>
          {:else if attr.wheelchair_play === 'no'}
            <Badge variant="neutralSoft" class="gap-[5px] whitespace-nowrap">
              <Accessibility class="h-3 w-3" aria-hidden="true" />{$_('details.wheelchairPlayNone')}
            </Badge>
          {/if}
          {#if attr.has_shade != null}
            <Badge variant={attr.has_shade ? 'successSoft' : 'dangerSoft'} class="whitespace-nowrap">
              {$_(attr.has_shade ? 'details.shaded' : 'details.notShaded')}
            </Badge>
          {/if}
        </div>
      {/if}

      <!-- At a glance -->
      <h3 class="section-label">{$_('details.atGlance')}</h3>
      <!-- Quick Facts Grid -->
      <div class="grid grid-cols-2 gap-x-4 gap-y-2 mb-4">
        {#if attr.area > 0}
          <div class="fact-item">
            <span class="info-label">{$_('details.sizeLabel')}</span>
            <span class="fact-value">{Math.round(attr.area / 10) * 10 || attr.area} m²</span>
          </div>
        {/if}

        <div class="fact-item">
          <span class="info-label">{$_('details.accessLabel')}</span>
          <span class="fact-value">{accessLabel}</span>
        </div>

        {#if surfaceLabel}
          <div class="fact-item">
            <span class="info-label">{$_('details.surfaceLabel')}</span>
            <span class="fact-value">{surfaceLabel}</span>
          </div>
        {/if}

        <!-- Only when tagged: 53 of 733 Fulda playgrounds carry `lit`, so
             "unknown" must stay silent rather than read as "not lit" (#934). -->
        {#if litFact}
          <div class="fact-item" data-fact="lit">
            <span class="info-label">{$_('pitch.lightingLabel')}</span>
            <span class="fact-value">{$_(litFact.key, litFact.values ? { values: litFact.values } : undefined)}</span>
          </div>
        {/if}

        {#if attr.tree_count > 0 || treeRows.length > 0}
          <div class="fact-item">
            <span class="info-label">{$_('details.treeShadow')}{treeRows.length > 0 ? ` (${treeRows.length})` : ''}</span>
            <span class="fact-value">
              {[
                attr.tree_count > 0 ? `${attr.tree_count} ${$_('hover.tagTrees')}` : null,
                treeRows.length > 0 ? treeRows.map(m => `${m} m`).join(', ') : null,
              ].filter(Boolean).join(' / ')}
            </span>
          </div>
        {/if}

      </div>

      <!-- Equipment overview: count summary with device theme chips folded onto the
           header row. Themes are device-derived, so they belong with equipment;
           inline icon-only chips (name via title/aria) keep the section compact. -->
      {#if equipSummaryItems.length || visibleThemes.length}
        <div class="equip-header" class:mb-1.5={equipSummaryItems.length} class:mb-4={!equipSummaryItems.length}>
          <h3 class="section-label mb-0">{$_('details.equipmentSection')}</h3>
          {#if visibleThemes.length}
            <div class="theme-row" aria-label={$_('details.themes')}>
              {#each visibleThemes as value (value)}
                <span
                  class="theme-chip theme-chip--icon"
                  title={themeName(value, $_)}
                  aria-label={themeName(value, $_)}
                >
                  <span class="theme-icon" aria-hidden="true">{themeIcon(value)}</span>
                </span>
              {/each}
              {#if themeOverflow}
                <span class="theme-chip theme-chip--more">+{themeOverflow}</span>
              {/if}
            </div>
          {/if}
        </div>
        {#if equipSummaryItems.length}
          <ul class="equip-summary mb-4">
            {#each equipSummaryItems as [key, count]}
              <li>{$_('equipment.' + key, { values: { count } })}</li>
            {/each}
          </ul>
        {/if}
      {/if}

      <!-- Contact Info -->
      {#if attr['contact:email'] || attr.email || attr['contact:phone'] || attr.phone || attr.operator}
        <h3 class="section-label">{$_('details.contactSection')}</h3>
        <div class="mb-4">
          {#if attr.operator}
            <div class="fact-item mb-1" data-testid="operator-value">
              <span class="info-label">{$_('details.operatorLabel')}</span>
              {#if attr['operator:wikidata']}
                <a href="https://www.wikidata.org/wiki/{attr['operator:wikidata']}"
                   target="_blank" rel="noopener" class="fact-value text-primary hover:underline">{attr.operator}</a>
              {:else}
                <span class="fact-value">{attr.operator}</span>
              {/if}
            </div>
          {/if}
          {#if attr['contact:phone'] || attr.phone || attr['contact:email'] || attr.email}
            <div class="contact-row">
              {#if attr['contact:phone'] || attr.phone}
                {@const phone = attr['contact:phone'] || attr.phone}
                {#if /^\+?\d/.test(phone.trim())}
                  <a href="tel:{phone}" class="contact-link">
                    <Phone class="h-3.5 w-3.5 shrink-0" /><span>{phone}</span>
                  </a>
                {:else}
                  <span class="contact-link contact-link--plain">
                    <Phone class="h-3.5 w-3.5 shrink-0" /><span>{phone}</span>
                  </span>
                {/if}
              {/if}
              {#if attr['contact:email'] || attr.email}
                {@const email = attr['contact:email'] || attr.email}
                {#if email.includes('@') && !/^javascript:/i.test(email.trim())}
                  <a href="mailto:{email}" class="contact-link">
                    <Mail class="h-3.5 w-3.5 shrink-0" /><span>{email}</span>
                  </a>
                {:else}
                  <span class="contact-link contact-link--plain">
                    <Mail class="h-3.5 w-3.5 shrink-0" /><span>{email}</span>
                  </span>
                {/if}
              {/if}
            </div>
          {/if}
        </div>
      {/if}

      <!-- MapComplete edit link -->
      <div class="mb-3">
        <MapCompleteLink href={mcUrl} label={$_('popup.editInMapComplete')} />
      </div>

      <!-- Accordion Sections -->
      <div class="border-t border-border">
        <!-- Photos -->
        <div class="border-b border-border">
          <button
            class="section-btn"
            onclick={() => toggleSection('photos')}
          >
            {#if openSections.has('photos')}
              <ChevronDown class="h-4 w-4 text-muted-foreground" />
            {:else}
              <ChevronRight class="h-4 w-4 text-muted-foreground" />
            {/if}
            <Image class="h-4 w-4 text-muted-foreground" />
            {$_('accordion.photos')}
          </button>
          {#if openSections.has('photos')}
            <div class="pb-3">
              {#if commonsTag || imageTag}
                <CommonsGallery commons={commonsTag} image={imageTag} bind:count={commonsPhotoCount} />
              {/if}
              <!-- Show Panoramax when it has its own photos, or as the sole
                   "add a photo" prompt when no Commons photos are on screen —
                   avoids both a stray "No photos yet" under a populated Commons
                   gallery and a fully blank section when a photo tag is present
                   but the gallery resolved nothing (unsafe URL / fetch fail). -->
              {#if panoramaxUuids.length > 0 || !commonsHasPhotos}
                <PanoramaxViewer uuids={panoramaxUuids} {mcUrl} />
              {/if}
            </div>
          {/if}
        </div>

        <!-- Equipment -->
        <div class="border-b border-border">
          <button
            class="section-btn"
            onclick={() => toggleSection('equipment')}
          >
            {#if openSections.has('equipment')}
              <ChevronDown class="h-4 w-4 text-muted-foreground" />
            {:else}
              <ChevronRight class="h-4 w-4 text-muted-foreground" />
            {/if}
            <Package class="h-4 w-4 text-muted-foreground" />
            {$_('accordion.equipment')}
          </button>
          {#if openSections.has('equipment')}
            <div class="pb-3">
              {#if equipmentLoading}
                <p class="text-sm text-muted-foreground italic py-2">{$_('details.loading')}</p>
              {:else}
                <EquipmentList features={equipmentFeatures} groups={equipmentGroups} playgroundAttr={attr} />
              {/if}
            </div>
          {/if}
        </div>

        <!-- POIs -->
        <div class="border-b border-border">
          <button
            class="section-btn"
            onclick={() => toggleSection('pois')}
          >
            {#if openSections.has('pois')}
              <ChevronDown class="h-4 w-4 text-muted-foreground" />
            {:else}
              <ChevronRight class="h-4 w-4 text-muted-foreground" />
            {/if}
            <Navigation class="h-4 w-4 text-muted-foreground" />
            {$_('accordion.surroundings')}
          </button>
          {#if openSections.has('pois')}
            <div class="pb-3">
              {#if poisLoading}
                <p class="text-sm text-muted-foreground italic py-2">{$_('details.loading')}</p>
              {:else}
                <POIPanel {pois} {centerLat} {centerLon} />
              {/if}
            </div>
          {/if}
        </div>

        <!-- Reviews -->
        <div class="border-b border-border">
          <button
            class="section-btn"
            onclick={() => toggleSection('reviews')}
          >
            {#if openSections.has('reviews')}
              <ChevronDown class="h-4 w-4 text-muted-foreground" />
            {:else}
              <ChevronRight class="h-4 w-4 text-muted-foreground" />
            {/if}
            <Star class="h-4 w-4 text-muted-foreground" />
            {$_('accordion.reviews')}
          </button>
          {#if openSections.has('reviews')}
            <div class="pb-3">
              <ReviewsPanel lat={centerLat} lon={centerLon} osmId={attr?.osm_id} name={attr?.name ?? null} radiusM={reviewRadiusM} />
            </div>
          {/if}
        </div>
      </div>
    </div>
  </aside>
{/if}

{#if dataAgePopoverOpen && dataAgeFormatted}
  <div
    id="data-age-popover"
    class="data-age-popover"
    style={dataAgePopoverStyle}
    role="dialog"
    aria-labelledby="data-age-popover-title"
  >
    <p id="data-age-popover-title" class="data-age-popover__title">{$_('details.osmDataAgeTitle')}</p>
    <p class="data-age-popover__body">
      {$_('details.osmDataAgeBody', { values: { age: dataAgeFormatted } })}
    </p>
  </div>
{/if}

<style>
  .info-panel {
    position: absolute;
    top: 0;
    left: 0;
    width: 380px;
    height: 100%;
    background: #ffffff;
    box-shadow: 4px 0 15px -3px rgb(0 0 0 / 0.1);
    z-index: 100;
    overflow-y: auto;
    display: none;
    flex-direction: column;
    border-right: 1px solid #e5e7eb;
    touch-action: pan-y;
    color-scheme: light;

    /* Force light theme variables */
    --color-background: #ffffff;
    --color-foreground: #1f2937;
    --color-card: #ffffff;
    --color-card-foreground: #1f2937;
    --color-popover: #ffffff;
    --color-popover-foreground: #1f2937;
    --color-muted: #f3f4f6;
    --color-muted-foreground: #6b7280;
    --color-border: #e5e7eb;
    --color-input: #e5e7eb;
  }

  .info-panel.lg\:flex {
    display: flex;
  }

  .info-panel__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 1rem;
    border-bottom: 1px solid #e5e7eb;
    position: sticky;
    top: 0;
    background: #ffffff;
    z-index: 1;
  }

  .info-panel__body {
    padding: 1rem;
    flex: 1;
  }

  @media (max-width: 1023px) {
    .info-panel {
      display: none !important;
    }
  }

  /* ── Typography ───────────────────────────────────────────────────────── */

  .panel-title {
    font-size: 17px;
    font-weight: 600;
    color: #1f2937;
    line-height: 1.3;
    margin: 0;
    /* The title shares its row with the action buttons and may shrink to
       nothing; a single long word ("Mehrzweckplatz" next to "Bring mich
       hierher") then ran underneath them. Hyphenate where the browser has a
       dictionary for the element's lang, otherwise break inside the word. */
    overflow-wrap: break-word;
    hyphens: auto;
  }


  .info-label {
    display: block;
    color: #9ca3af;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .fact-item {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  .fact-value {
    font-size: 13px;
    color: #1f2937;
  }

  .section-btn {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 0;
    background: transparent;
    border: none;
    cursor: pointer;
    color: #9ca3af;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    transition: color 0.15s, background 0.15s;
    text-align: left;
  }

  .section-btn:hover {
    color: #374151;
    background: #fafafa;
  }

  .panel-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.25rem;
    height: 2.25rem;
    border: none;
    background: transparent;
    border-radius: 0.375rem;
    cursor: pointer;
    color: #6b7280;
    transition: background 0.15s, color 0.15s;
  }

  .panel-icon-btn:hover {
    background: #f3f4f6;
    color: #1f2937;
  }

  /* ── Action row ─────────────────────────────────────── */
  .action-row {
    display: flex;
    gap: 8px;
    padding: 10px 1rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .action-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border: none;
    border-radius: 9px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    transition: background 0.15s, opacity 0.15s;
  }

  .action-btn--primary {
    flex: 1;
    padding: 10px 0;
    background: var(--color-primary);
    color: #fff;
  }
  .action-btn--primary:hover { background: var(--color-primary-hover); }

  .action-btn--icon {
    width: 44px;
    height: 44px;
    background: #f3f4f6;
    color: #6b7280;
    border: 1px solid #e5e7eb;
  }
  .action-btn--icon:hover { background: #e5e7eb; color: #1f2937; }

  /* ── Contact row (phone + email inline) ─────────────── */
  .contact-row {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .contact-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    color: var(--color-primary);
    text-decoration: none;
    min-width: 0;
  }
  .contact-link span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .contact-link:hover { text-decoration: underline; }
  .contact-link--plain { color: #1f2937; cursor: default; }
  .contact-link--plain:hover { text-decoration: none; }

  /* ── Status row (opening hours + age chip) ──────────── */
  .status-row {
    display: flex;
    /* Chips must wrap: the row can hold opening hours, baby, toddler,
       wheelchair and shade at once, which overflows the panel on any
       realistic width. Without this they are clipped at the right edge
       instead of moving to a second line. */
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }

  .theme-banner {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 12px;
    border-radius: 10px;
    background: linear-gradient(135deg, #eef2ff, #faf5ff);
    border: 1px solid #e0e7ff;
  }
  .theme-banner__icon { font-size: 1.5rem; line-height: 1; }
  .theme-banner__label {
    font-size: 0.95rem;
    font-weight: 600;
    color: #3730a3;
  }

  .section-label {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #9ca3af;
    margin: 0 0 6px;
  }

  .equip-summary {
    list-style: none;
    padding: 0;
    margin: 0 0 1rem;
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    font-size: 13px;
    color: #374151;
  }

  .equip-header {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 10px;
  }
  .theme-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .theme-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 9px 3px 7px;
    border-radius: 999px;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    font-size: 0.78rem;
    color: #374151;
    line-height: 1.3;
  }
  /* Icon-only chip: tighter, symmetric padding. Declared after `.theme-chip`
     so it wins on source order (equal specificity). */
  .theme-chip--icon {
    padding: 3px 6px;
  }
  .theme-icon { font-size: 1rem; }
  .theme-chip--more {
    color: #6b7280;
    font-variant-numeric: tabular-nums;
  }

  .status-dot {
    width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0;
  }
  .status-dot--open   { background: var(--color-primary); }
  .status-dot--closed { background: #ef4444; }

  /* ── Stat cards grid ─────────────────────────────── */
  .stat-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  .stat-card {
    display: flex;
    flex-direction: column;
    gap: 3px;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 10px 12px;
  }

  .stat-value {
    font-size: 15px;
    font-weight: 700;
    color: #1f2937;
    line-height: 1.2;
  }

  .stat-label {
    font-size: 10px;
    font-weight: 700;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .backend-name {
    margin: 0.15rem 0 0;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #9ca3af;
  }

  /* ── Data-age chip + fixed popover ──────────────────────────────────── */
  .detail-dot {
    display: inline-block;
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    flex-shrink: 0;
  }

  /* A callout rather than a grey caption line, which went unnoticed among the
     panel's other content (#982). Blue, not amber: the hint is an invitation,
     and a warning colour next to the badge would read as the verdict the
     mapping-detail palette avoids. Pulled up against the status row's mb-4 so
     it stays attached to the badge. */
  .detail-hint {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: -0.25rem 0 1rem;
    padding: 8px 10px;
    border-radius: 8px;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    font-size: 0.8125rem;
    line-height: 1.4;
    color: #1e3a8a;
  }

  .data-age-chip {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 2px 7px;
    border-radius: 9999px;
    font-size: 10px;
    font-weight: 600;
    color: #6b7280;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s, color 0.15s;
    line-height: 1.4;
  }

  .data-age-chip:hover,
  .data-age-chip[aria-expanded="true"] {
    background: #e5e7eb;
    color: #374151;
  }

  /* Rendered outside the aside so overflow-y: auto can't clip it. */
  :global(.data-age-popover) {
    position: fixed;
    z-index: 500;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    padding: 10px 12px;
  }

  :global(.data-age-popover__title) {
    font-size: 10px;
    font-weight: 700;
    color: #374151;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin: 0 0 4px;
  }

  :global(.data-age-popover__body) {
    font-size: 12px;
    color: #6b7280;
    line-height: 1.5;
    margin: 0;
  }

  /* ── Navigation button ────────────────────────────────────────────────── */
  .navigate-btn {
    padding: 6px 12px;
    background: var(--color-primary);
    color: var(--color-primary-foreground);
    border: none;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    text-decoration: none;
    transition: background 0.15s;
  }
  .navigate-btn:hover {
    background: var(--color-primary-hover);
    color: var(--color-primary-foreground);
  }
</style>
